/**
 * The cellar as a CSV file (T094, spec.md User Story 11, FR-042, FR-043, contracts/csv-export.md):
 * built in the browser from the loaded cellar, for a French spreadsheet. The format is data, not
 * screen text, so it stays in French whatever the person's language.
 *
 * - One line per purchase ("Achat"), per tasting ("Dégustation"), and per wine with neither
 *   ("Vin"), each repeating the wine's identity, stock and drink-until year. Since schema
 *   version 6 (owner, 2026-10-06): "Date" is the purchase's day when known, and there is no
 *   drink-by month. Since version 7 (owner, 2026-10-09): no "Vendeur" (sellers are erased), and
 *   a tasting's "Quantité" is the bottles it took from stock.
 * - Wines by Domaine, Année, Cépage (ignoring accents and case); a wine's lines by month, then
 *   entry order.
 * - UTF-8 with a byte-order mark, semicolons, decimal commas, CRLF; cells that do not apply empty.
 * - Text a spreadsheet would run as a formula gets a leading apostrophe.
 */
import {
  wineStock,
  type Cellar,
  type Purchase,
  type Tasting,
  type TypeDeVin,
  type Wine,
} from './cellar';
import { COUNTRIES } from './countries';
import { bottlesDrunk } from './stock';
import { regionName } from './regions';
import { compareMonths, currentMonth, formatMonthValue, type MonthValue } from './months';

export const CSV_HEADINGS = [
  'Ligne',
  'Domaine',
  'Cépage',
  'Parcelle',
  'Année',
  'Pays',
  'Région',
  'Type de vin',
  'Stock actuel',
  "À boire jusqu'en (année)",
  'Date',
  'Quantité',
  'Prix unitaire (€)',
  "Détails de l'achat",
  'Appréciation',
  'Notes de dégustation',
  'Hors stock',
];

const WINE_TYPES_FR: Record<TypeDeVin, string> = {
  rouge: 'Rouge',
  blanc: 'Blanc',
  rose: 'Rosé',
  vendanges_tardives: 'Vendanges tardives',
};

const BOM = '﻿';
const SEPARATOR = ';';
const LINE_END = '\r\n';

/** A leading apostrophe on text a spreadsheet would treat as a formula (FR-043). */
function safeText(value: string | undefined): string {
  if (value === undefined) return '';
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/** Quotes a cell holding a separator, quote or line break; quotes are doubled. */
function cell(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** A purchase's date: "2026-10-04" when its day is known (schema version 6), else "2026-10". */
function purchaseDate(purchase: Purchase): string {
  const month = formatMonthValue(purchase.purchasedOn);
  return purchase.purchasedDay !== undefined
    ? `${month}-${String(purchase.purchasedDay).padStart(2, '0')}`
    : month;
}

const decimalComma = (value: number) => String(value).replace('.', ',');
/** Whole euros, never a decimal (owner, 2026-10-15); an older price with cents is rounded. */
const euros = (cents: number) => String(Math.round(cents / 100));

const compareText = (a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base' });

function compareWines(a: Wine, b: Wine): number {
  return compareText(a.domaine, b.domaine) || a.annee - b.annee || compareText(a.cepage, b.cepage);
}

type Entry =
  | { kind: 'Achat'; month: MonthValue; createdAt: number; purchase: Purchase }
  | { kind: 'Dégustation'; month: MonthValue; createdAt: number; tasting: Tasting };

function wineLines(wine: Wine): string[][] {
  const identity = [
    safeText(wine.domaine),
    safeText(wine.cepage),
    safeText(wine.parcelle),
    String(wine.annee),
    COUNTRIES.find((country) => country.code === wine.pays)?.name ?? wine.pays,
    safeText(wine.region !== undefined ? regionName(wine.region, 'Autre région') : undefined),
    WINE_TYPES_FR[wine.typeDeVin],
    String(wineStock(wine)),
    wine.drinkUntilYear !== undefined ? String(wine.drinkUntilYear) : '',
  ];

  const entries: Entry[] = [
    ...wine.purchases.map((purchase): Entry => ({
      kind: 'Achat',
      month: purchase.purchasedOn,
      createdAt: purchase.createdAt,
      purchase,
    })),
    ...wine.tastings.map((tasting): Entry => ({
      kind: 'Dégustation',
      month: tasting.tastedOn,
      createdAt: tasting.createdAt,
      tasting,
    })),
  ];
  // Stable sort: equal months and times keep purchases, then tastings, in their stored order.
  entries.sort((a, b) => compareMonths(a.month, b.month) || a.createdAt - b.createdAt);

  if (entries.length === 0) return [['Vin', ...identity, '', '', '', '', '', '', '']];

  return entries.map((entry) =>
    entry.kind === 'Achat'
      ? [
          'Achat',
          ...identity,
          purchaseDate(entry.purchase),
          String(entry.purchase.quantity),
          euros(entry.purchase.unitPriceCents),
          safeText(entry.purchase.notes),
          '',
          '',
          '',
        ]
      : [
          'Dégustation',
          ...identity,
          formatMonthValue(entry.month),
          entry.tasting.consumedFromStock ? String(bottlesDrunk(entry.tasting)) : '',
          '',
          '',
          entry.tasting.rating !== undefined ? decimalComma(entry.tasting.rating) : '',
          safeText(entry.tasting.notes),
          entry.tasting.consumedFromStock ? 'Non' : 'Oui',
        ],
  );
}

export function buildCellarCsv(cellar: Cellar): string {
  const rows = [CSV_HEADINGS, ...[...cellar.wines].sort(compareWines).flatMap(wineLines)];
  return BOM + rows.map((row) => row.map(cell).join(SEPARATOR) + LINE_END).join('');
}

/**
 * `cave-{nom}-{AAAA-MM}.csv`: the cellar person's name, or the part of their email before the @,
 * without accents or anything a file name may not hold; the current month in Paris.
 */
export function exportFileName(name: string, now: Date = new Date()): string {
  const safe = (name.includes('@') ? name.slice(0, name.indexOf('@')) : name)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    // A cellar named "Cave de Papa" gives "cave-de-papa-…", not "cave-cave-de-papa-…".
    .replace(/^cave(?:-|$)/, '');
  const month = formatMonthValue(currentMonth(now));
  return safe === '' ? `cave-${month}.csv` : `cave-${safe}-${month}.csv`;
}
